import {
  useMutation,
  type UseMutationOptions,
  useQuery,
  useQueryClient,
  type UseQueryOptions
} from "@tanstack/react-query"

import {
  createAddress,
  deleteAddress,
  getAddresses,
  getDistricts,
  getProvinces,
  getWards,
  setDefaultAddress,
  updateAddress
} from "@/api"
import type {
  Address,
  CreateAddressInput,
  District,
  Province,
  UpdateAddressInput,
  Ward
} from "@/modules/types"
import type { SchemaResponse } from "@/modules/types/common/schema"

// Query keys
export const ADDRESS_KEYS = {
  all: ["addresses"] as const,
  detail: (id: string) => ["addresses", id] as const
}

export const LOCATION_KEYS = {
  provinces: ["provinces"] as const,
  districts: (provinceCode: string) => ["districts", provinceCode] as const,
  wards: (districtCode: string) => ["wards", districtCode] as const
}

// Get all addresses
export const useAddresses = (options?: UseQueryOptions<SchemaResponse<Address[]>, Error>) => {
  return useQuery<SchemaResponse<Address[]>, Error>({
    queryKey: ADDRESS_KEYS.all,
    queryFn: async () => {
      const response = await getAddresses()
      return response.data
    },
    ...options
  })
}

// Create address
export const useCreateAddress = (
  options?: UseMutationOptions<SchemaResponse<Address>, Error, CreateAddressInput>
) => {
  const queryClient = useQueryClient()

  return useMutation<SchemaResponse<Address>, Error, CreateAddressInput>({
    mutationFn: async (data: CreateAddressInput) => {
      const response = await createAddress(data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ADDRESS_KEYS.all })
    },
    ...options
  })
}

// Update address
export const useUpdateAddress = (
  options?: UseMutationOptions<
    SchemaResponse<Address>,
    Error,
    { id: string; data: UpdateAddressInput }
  >
) => {
  const queryClient = useQueryClient()

  return useMutation<SchemaResponse<Address>, Error, { id: string; data: UpdateAddressInput }>({
    mutationFn: async ({ id, data }) => {
      const response = await updateAddress(id, data)
      return response.data
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ADDRESS_KEYS.all })
      queryClient.invalidateQueries({ queryKey: ADDRESS_KEYS.detail(variables.id) })
    },
    ...options
  })
}

// Delete address
export const useDeleteAddress = (
  options?: UseMutationOptions<SchemaResponse<null>, Error, string>
) => {
  const queryClient = useQueryClient()

  return useMutation<SchemaResponse<null>, Error, string>({
    mutationFn: async (id: string) => {
      const response = await deleteAddress(id)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ADDRESS_KEYS.all })
    },
    ...options
  })
}

// Set default address
export const useSetDefaultAddress = (
  options?: UseMutationOptions<SchemaResponse<null>, Error, string>
) => {
  const queryClient = useQueryClient()

  return useMutation<SchemaResponse<null>, Error, string>({
    mutationFn: async (id: string) => {
      const response = await setDefaultAddress(id)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ADDRESS_KEYS.all })
    },
    ...options
  })
}

// Location queries
export const useProvinces = (options?: UseQueryOptions<Province[], Error>) => {
  return useQuery<Province[], Error>({
    queryKey: LOCATION_KEYS.provinces,
    queryFn: async () => {
      const response = await getProvinces()
      return response?.data?.data?.list || []
    },
    ...options
  })
}

export const useDistricts = (
  provinceCode: string | undefined,
  options?: UseQueryOptions<District[], Error>
) => {
  return useQuery<District[], Error>({
    queryKey: provinceCode ? LOCATION_KEYS.districts(provinceCode) : ["districts"],
    queryFn: async () => {
      if (!provinceCode) throw new Error("Province code is required")
      const response = await getDistricts(provinceCode)
      return response.data.data?.list || []
    },
    enabled: !!provinceCode,
    ...options
  })
}

export const useWards = (
  districtCode: string | undefined,
  options?: UseQueryOptions<Ward[], Error>
) => {
  return useQuery<Ward[], Error>({
    queryKey: districtCode ? LOCATION_KEYS.wards(districtCode) : ["wards"],
    queryFn: async () => {
      if (!districtCode) throw new Error("District code is required")
      const response = await getWards(districtCode)
      return response.data.data?.list || []
    },
    enabled: !!districtCode,
    ...options
  })
}
